/**
 * @module Components.Routes
 *
 */
import React, {
  useRef,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";
import { useHistory } from "react-router-dom";
import { useActions } from "app/utils/hooks";
import { UserContext, RoleType } from "app/contexts/UserContext";
import useFacilityLessonRates from "app/routes/Sessions/hooks/useFacilityLessonRates";

import { calendarDateFormat } from "app/utils/luxonFormats";
import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";
import useInterval from "app/utils/hooks/useInterval";
import { Loading } from "app/components/Wrappers";
import { useCoachProfile } from "./indexModel";
import useAvailablity from "./hooks/useAvailability";
import useConfirmation from "./hooks/useConfirmation";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";
import useSlotInterval from "app/utils/hooks/scheduling/useSlotInterval";

import {
  hideInfoPopup,
  fetchEvents,
  initializeNewEvent,
  deselectEvent,
} from "app/actions/events";
import { editProfileSettings, fetchSelf } from "app/actions/user";
import CalendarSettingsPopup from "app/components/CalendarSettingsPopup";
import { shouldShowCalendarSettingsPopup } from "app/components/CalendarSettingsPopup/gate";
import { RootState } from "typedefs";
import { EventType } from "app/models/EventModel";

import EventInfo from "./EventInfo";
import CalendarToolbar from "./CalendarToolbar";
import EventForm from "./EventForm";
import InfoModal from "./InfoModal";
import ConfirmationPrompt from "./ConfirmationPrompt";
import CustomRefundModal from "./CustomRefundModal";

import useCalendarState from "./hooks/useCalendarState";
import useCalendarNavigation from "./hooks/useCalendarNavigation";
import useEventHandlers from "./hooks/useEventHandlers";

import DayView from "./views/DayView";
import WeekView from "./views/WeekView";
import MonthView from "./views/MonthView";
import ScheduleView from "./views/ScheduleView";
import SideDatePicker from "./SideDatePicker";
import styles from "./styles.module.scss";

const UNSELECT_CANCEL =
  ".event_form_container, .confirm_cancel, .confirm_cancel_conflict, .info-modal-content, .info-modal-header, .time-range-menu-dropdown, .invite_new_student_modal, .react-datepicker-popper, .react-datepicker, .rs__menu";

function minutesToHHMMSS(minutes: number) {
  const hours = Math.floor(minutes / 60)
    .toString()
    .padStart(2, "0");
  const mins = (minutes % 60).toString().padStart(2, "0");
  return `${hours}:${mins}:00`;
}

export default function Calendar() {
  const calendarRef = useRef(null);
  const eventElRef = useRef(null);
  const scheduleOnDateChangeRef = useRef<((date: DateTime) => void) | null>(
    null,
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useContext(UserContext);

  const currentFacility = useCurrentFacility();
  const { snapDuration } = useSlotInterval();

  const facilityBookingEnabled =
    user.facility?.featureFlags?.facilityLevelBookingOptions === true;
  const { rates: facilityLessonRates } = useFacilityLessonRates(
    user.facility?.id,
    facilityBookingEnabled,
  );
  const { preferences, updatePreferences, refresh } = useCalendarPreferences();

  const coachProfile = useCoachProfile();

  const state = useCalendarState({
    preferences,
    facilityBookingEnabled,
    facilityLessonRates,
    coachLessonRates: coachProfile.data?.lessonRates || null,
  });

  useEffect(() => {
    refresh();
  }, [refresh, state.calendarView]);

  const eventActions = useActions({ fetchEvents });

  const timeZone = coachProfile.data?.timeZone || "";

  // Use facility rates when FF enabled, otherwise use coach rates
  const lessonRates = facilityBookingEnabled
    ? facilityLessonRates
    : [...(coachProfile.data?.lessonRates || [])].sort((a, b) => {
        const orderA = a.orderNumber ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.orderNumber ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });

  const { availability } = useAvailablity(
    coachProfile.data,
    state.calendarInterval.start,
    state.calendarInterval.end?.minus({ days: 1 }),
  );

  const { PromptComponent, requestConfirmation } = useConfirmation(
    (props: any) => <ConfirmationPrompt {...props} />,
  );

  const {
    PromptComponent: RefundPromptComponent,
    requestConfirmation: refundRequestConfirmation,
  } = useConfirmation((props: any) => <CustomRefundModal {...props} />);

  const updateEvents = useCallback(() => {
    if (!state.isoStartDate || !state.isoEndDate) {
      return;
    }
    if (state.calendarView === "listWeek") {
      return;
    }
    eventActions.fetchEvents({
      start: state.isoStartDate,
      end: state.isoEndDate,
    });
  }, [state.isoStartDate, state.isoEndDate, state.calendarView]);

  useInterval(updateEvents, 60 * 1000);

  const navigation = useCalendarNavigation({
    calendarRef,
    calendarView: state.calendarView,
    timeZone,
    calendarInterval: state.calendarInterval,
    setCalendarInterval: state.setCalendarInterval,
    currentDay: state.currentDay,
    setCurrentDay: state.setCurrentDay,
    updatePreferences,
  });

  const eventHandlers = useEventHandlers({
    calendarRef,
    selectedEvent: state.selectedEvent,
    calendarView: state.calendarView,
    currentDay: state.currentDay,
    timeZone,
    lessonRates,
    requestConfirmation,
    refundRequestConfirmation,
  });

  const handleSelect = useCallback(
    (info: any) => {
      const tz = info.view.dateEnv.timeZone;
      const defaultBookingOption = lessonRates?.find(
        (rate) => rate.defaultRate === true,
      );

      const startTime = DateTime.fromISO(info.startStr).setZone(tz);
      const durationInMinutes =
        parseInt(defaultBookingOption?.duration, 10) || 0;

      const endTime = startTime.plus({ minutes: durationInMinutes });

      const endTimeISO = new Date(
        endTime.toISO({ suppressMilliseconds: true }),
      ).toISOString();
      const infoEndStrISO = new Date(info.endStr).toISOString();

      const lessonRateId =
        endTimeISO === infoEndStrISO ? defaultBookingOption?.id : null;

      const rate = lessonRateId ? defaultBookingOption?.rate : null;
      const location = lessonRateId ? defaultBookingOption?.location : null;

      dispatch(
        initializeNewEvent({
          date: DateTime.fromISO(info.startStr).setZone(tz).startOf("day"),
          start: info.startStr,
          end: info.endStr,
          type: EventType.LESSON_SCHEDULE,
          backgroundColor: "#0279b3",
          timeZone: tz,
          lessonRateId,
          rate,
          location,
        }),
      );
    },
    [dispatch, lessonRates],
  );

  const handleUnselect = useCallback(
    () => dispatch(deselectEvent()),
    [dispatch],
  );

  const handleMonthDateClick = useCallback(
    (date: DateTime) => {
      state.setCurrentDay(date);
      navigation.onViewChange("timeGridDay");
    },
    [state.setCurrentDay, navigation.onViewChange],
  );

  const resolvedSnap = state.isSelecting
    ? snapDuration
    : minutesToHHMMSS(state.defaultDurationInMinutesCoach);

  const handleIntervalChange = useCallback(
    (start: DateTime, end: DateTime) => {
      state.setCalendarInterval({ start, end });
    },
    [state.setCalendarInterval],
  );

  const handleSideDateChange = useCallback(
    (date: DateTime) => {
      if (
        state.calendarView === "listWeek" &&
        scheduleOnDateChangeRef.current
      ) {
        scheduleOnDateChangeRef.current(date);
      } else {
        navigation.onDateChangeCalendar(date);
      }
    },
    [state.calendarView, navigation.onDateChangeCalendar],
  );

  const userSelf = useSelector((state: RootState) => state.user.self);
  // Figma 410:21980: shown once to an Academy coach on their first visit.
  const [settingsPopupDismissed, setSettingsPopupDismissed] = useState(false);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);
  const showSettingsPopup =
    !settingsPopupDismissed && shouldShowCalendarSettingsPopup(userSelf);

  const handleDismissSettingsPopup = useCallback(async () => {
    setSettingsPopupDismissed(true);
    try {
      await dispatch(
        editProfileSettings({ calendar_settings_popup_shown: true }),
      );
      await dispatch(fetchSelf(true));
    } catch (err) {
      console.error("Failed to update calendar settings popup flag", err);
    }
  }, [dispatch]);

  // Early return after all hooks
  if (coachProfile.pending) {
    return (
      <Loading loadType="spinner" isLoading className={styles.loadingSpinner} />
    );
  }

  const handleCalendarSettings = () => {
    if (coachProfile.data.allowScheduleProviderAccess) {
      history.push("/calendar/settings");
    } else {
      history.push("/calendar/pricing");
    }
  };

  const showSidePanel =
    state.calendarView === "timeGridDay" || state.calendarView === "listWeek";

  return (
    <div className={styles.container} data-testid="calendar-container">
      <CalendarSettingsPopup
        isOpen={showSettingsPopup}
        onClose={handleDismissSettingsPopup}
        settingsButtonRef={settingsButtonRef}
      />

      {state.showEventForm && (
        <EventForm
          event={state.eventForForm}
          lessonRateOptions={lessonRates}
          onCancel={eventHandlers.onCancelSelection}
          onSave={eventHandlers.onSaveEventForm}
          submitting={state.pendingCreate || state.pendingEdit}
          errors={state.eventSubmitErrors}
          coachProfile={coachProfile.data}
          facility={currentFacility.data}
          facilityBookingEnabled={facilityBookingEnabled}
        />
      )}

      <InfoModal
        isOpen={state.showInfoPopup}
        title={state.infoPopupContent.title}
        status={state.infoPopupContent.status}
        onDismiss={() => dispatch(hideInfoPopup())}
        payload={state.infoPopupContent.payload}
        action={state.infoPopupContent.action}
        canOverride={state.infoPopupContent.canOverride}
        ownEvents
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div>{state.infoPopupContent.content}</div>
          <div>
            <b style={{ color: "#000" }}>
              {" "}
              {state.infoPopupContent.subcontent}
            </b>
          </div>
        </div>
      </InfoModal>

      <PromptComponent />

      <RefundPromptComponent />

      <div
        id="calendar-element"
        className={`${styles.calendar_container} calendar-page-container`}
      >
        <CalendarToolbar
          date={state.dateCalendar}
          dateFormat={calendarDateFormat}
          onNext={navigation.onNextCalendar}
          onPrev={navigation.onPrevCalendar}
          onDateChange={handleSideDateChange}
          handleCalendarSettings={handleCalendarSettings}
          settingsButtonRef={settingsButtonRef}
          initNewEvent={eventHandlers.initNewEvent}
          calendarView={state.calendarView}
          currentView={preferences.calendarView}
          onViewChange={navigation.onViewChange}
          showFacilityCalendar={user.facility?.coachProfiles?.length > 1}
          showResourceCalendar={
            user.facility?.featureFlags?.resourceManagement === true
          }
          hideViewSelector={
            user.role === RoleType.ADMIN || user.role === RoleType.SCHEDULER
          }
          hideDatePickerOnLarge={showSidePanel}
        />

        <EventInfo
          timeZone={timeZone}
          event={state.selectedEvent}
          referenceElement={eventElRef.current}
          onDelete={eventHandlers.onDeleteEvent}
          onEdit={
            state.selectedEvent?.type === "program"
              ? eventHandlers.onEditProgram
              : eventHandlers.onEditEvent
          }
          onCopy={eventHandlers.onCopyEvent}
        />

        {showSidePanel ? (
          <div className={styles.calendar_content_row}>
            <div className={`${styles.side_panel} desktop-only-large`}>
              <SideDatePicker
                date={state.dateCalendar}
                onDateChange={handleSideDateChange}
              />
            </div>
            <div
              className={`${styles.side_panel_divider} desktop-only-large`}
            />
            <div className={styles.calendar_main_content}>
              {state.calendarView === "listWeek" && (
                <ScheduleView
                  calendarRef={calendarRef}
                  timeZone={timeZone}
                  preferences={preferences}
                  eventElRef={eventElRef}
                  selectedDate={state.currentDay}
                  setCurrentDay={state.setCurrentDay}
                  onDateChangeRef={scheduleOnDateChangeRef}
                />
              )}

              {state.calendarView === "timeGridDay" && (
                <div
                  className={styles.calendarMouseActions}
                  onMouseDown={state.handleMouseDown}
                  onMouseUp={state.handleMouseUp}
                >
                  <DayView
                    calendarRef={calendarRef}
                    timeZone={timeZone}
                    preferences={preferences}
                    eventElRef={eventElRef}
                    initialDate={state.dateCalendar}
                    currentDay={state.currentDay}
                    isMobile={state.isMobile}
                    businessHours={availability}
                    onIntervalChange={handleIntervalChange}
                    onSelect={handleSelect}
                    onUnselect={handleUnselect}
                    unselectCancel={UNSELECT_CANCEL}
                    snapDuration={resolvedSnap}
                    onDateChange={
                      state.isMobile
                        ? navigation.onDateChangeCalendarMobile
                        : navigation.onDateChangeDayView
                    }
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {state.calendarView === "timeGridWeek" && (
              <div
                className={styles.calendarMouseActions}
                onMouseDown={state.handleMouseDown}
                onMouseUp={state.handleMouseUp}
              >
                <WeekView
                  calendarRef={calendarRef}
                  timeZone={timeZone}
                  preferences={preferences}
                  eventElRef={eventElRef}
                  initialDate={state.dateCalendar}
                  businessHours={availability}
                  onIntervalChange={handleIntervalChange}
                  onSelect={handleSelect}
                  onUnselect={handleUnselect}
                  unselectCancel={UNSELECT_CANCEL}
                  snapDuration={resolvedSnap}
                />
              </div>
            )}

            {state.calendarView === "dayGridMonth" && (
              <MonthView
                calendarRef={calendarRef}
                timeZone={timeZone}
                preferences={preferences}
                eventElRef={eventElRef}
                initialDate={state.dateCalendar}
                onIntervalChange={handleIntervalChange}
                onDateClick={handleMonthDateClick}
                businessHours={availability}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
